/**
 * AccessConfig zod mirror (Permissions v2 — B1b).
 *
 * The WRITE-path validator for the custom permissions config: settings PATCH bodies
 * and the per-task socket control payload. Mirrors the agent-sdk `AccessConfig` type
 * (packages/agent-sdk/src/policy/access-config.ts) — intentionally duplicated, like
 * AccessLevelSchema above it: shared must stay independent of agent-sdk (agent-sdk
 * already imports shared's flags subpath, so the reverse import would be circular).
 * READS from stored jsonb stay parseAccessConfig's job (lenient, total).
 *
 * Payments are never configurable — enforced here as:
 *   - `families.payment` REJECTS (payment is excluded from the key enum);
 *   - `apps.*.payment` (and any unknown family key) is STRIPPED by the plain-object
 *     entry schema, matching compileAccessConfig/parseAccessConfig behavior so a
 *     sanitized write round-trips byte-identical through the engine.
 */

import { z } from 'zod';
import { AccessLevelSchema } from './common.schema.js';

const FamilyToggleSchema = z.enum(['ask', 'allow']);
const AppActionSchema = z.enum(['ask', 'allow', 'block']);

/** The five configurable side-effect families — payment is deliberately absent. */
const ConfigurableFamilySchema = z.enum([
  'readonly',
  'workspace_write',
  'external_send',
  'external_mutation',
  'dangerous_delete',
]);

/**
 * One per-app (Composio toolkit) pin set. A plain z.object STRIPS unknown keys, so
 * `payment` and future-family keys drop silently here rather than failing the write.
 */
const AppFamilyActionsSchema = z.object({
  readonly: AppActionSchema.optional(),
  workspace_write: AppActionSchema.optional(),
  external_send: AppActionSchema.optional(),
  external_mutation: AppActionSchema.optional(),
  dangerous_delete: AppActionSchema.optional(),
});

/** The custom permissions config: a preset level or 'custom' + family/app overrides. */
export const AccessConfigSchema = z.object({
  level: z.union([AccessLevelSchema, z.literal('custom')]),
  families: z.partialRecord(ConfigurableFamilySchema, FamilyToggleSchema).optional(),
  apps: z.record(z.string(), AppFamilyActionsSchema).optional(),
});

/**
 * The AccessConfig TYPE for clients that cannot depend on agent-sdk (web). Inferred
 * from the schema so it can never drift from the write-path validator; structurally
 * matches the agent-sdk `AccessConfig` the engine consumes.
 */
export type AccessConfig = z.infer<typeof AccessConfigSchema>;
